import { GpxImportErrorCode, isMalformedXml } from '../../utils/helpers/gpxImportError';
import { decodeGpxName } from '../../utils/helpers/decodeGpxText';

/** One point as the importer uses it, after the parser's own shape is normalised. */
export interface GpxTrackPoint {
  lat: number;
  lon: number;
  elevation: number;
  time: unknown;
}

/** One point as `parseGpxTracks` (and `gpxparser` before it) returns it. */
interface ParsedPoint {
  lat: number;
  lon: number;
  ele?: number | null;
  time?: unknown;
}

/**
 * Why a GPX file cannot be imported as a recording, decided in one place.
 *
 * These checks used to sit inline in ImportRecordingDialog's `parseGpxFile`,
 * where the only way to exercise them was to mount the component and feed a
 * FileReader. They are pure decisions over parsed data, so they live here and
 * the component reads the answer — which is the shape CLAUDE.md's Test Policy
 * asks for, and the reason the timestamp rule below can be pinned at all.
 */

/**
 * The text-level checks, before the parser runs.
 *
 * `isMalformedXml` is separate from "empty" because DOMParser does not throw on
 * damaged XML — it injects a <parsererror> node, so a broken file parses to zero
 * features and is indistinguishable from an empty one unless it is asked.
 */
export function gpxTextImportError(text: string | null | undefined): GpxImportErrorCode | null {
  if (!text || text.trim() === '') return GpxImportErrorCode.Empty;
  if (isMalformedXml(text)) return GpxImportErrorCode.MalformedXml;

  return null;
}

/**
 * The track-level checks, after it has parsed.
 *
 * The timestamp rule is the recording/route distinction: a planner export has
 * coordinates but no per-point time. Rejecting it here, rather than importing a
 * timeless "recording", is what keeps peak detection honest — its dwell gate has
 * nothing to work with otherwise, and every peak on the route would either all
 * bag or none would.
 *
 * "Some point has a time" rather than "every point does": real GPS tracks drop
 * the odd sample, and a file that is 99% timed is a recording by any reading.
 */
export function gpxTrackImportError(points: GpxTrackPoint[] | null | undefined): GpxImportErrorCode | null {
  if (!points || points.length === 0) return GpxImportErrorCode.NoRouteOrTrack;
  if (!points.some((point) => point.time)) return GpxImportErrorCode.NoTimestamps;

  return null;
}

/** What `parseGpxTracks` hands back: the slice of `gpxparser`'s result the dialog reads. */
export interface ParsedGpx {
  metadata: { name?: string };
  tracks: { points: ParsedPoint[] }[];
}

/**
 * Reads the tracks and the metadata name out of a GPX document.
 *
 * This replaces `gpxparser` for recordings. That library summarises every track's
 * elevation with `Math.max.apply(null, elevations)`, which passes each elevation as
 * a separate argument; past roughly 100k points the engine refuses and the import
 * dies with "Maximum call stack size exceeded" before any of our code runs. A
 * four-month London-to-Geneva walk is 175k points. Nothing here builds an argument
 * list from the points, so the size of the file is the only limit.
 *
 * The result keeps `gpxparser`'s shape (`ele` null when absent or unparseable,
 * `time` a Date or null, the name as the element's innerHTML for decodeGpxName),
 * so everything downstream of the parse is unchanged.
 */
export function parseGpxTracks(text: string): ParsedGpx {
  const doc = new DOMParser().parseFromString(text, 'text/xml');

  const metadataName = doc.getElementsByTagName('metadata')[0]
    ?.getElementsByTagName('name')[0];

  // querySelectorAll, not getElementsByTagName: the latter is a live collection, and
  // copying one out is quadratic in some DOMs (jsdom took 15s over 20k points).
  const tracks = Array.from(doc.querySelectorAll('trk')).map((trk) => ({
    points: Array.from(trk.querySelectorAll('trkpt')).map(toParsedPoint),
  }));

  return {
    metadata: metadataName ? { name: metadataName.innerHTML } : {},
    tracks,
  };
}

function toParsedPoint(trkpt: Element): ParsedPoint {
  let eleText: string | null = null;
  let timeText: string | null = null;
  for (let child = trkpt.firstElementChild; child; child = child.nextElementSibling) {
    if (child.localName === 'ele') eleText = child.textContent;
    else if (child.localName === 'time') timeText = child.textContent;
  }

  const ele = eleText == null ? NaN : parseFloat(eleText);

  return {
    lat: parseFloat(trkpt.getAttribute('lat') ?? ''),
    lon: parseFloat(trkpt.getAttribute('lon') ?? ''),
    ele: Number.isNaN(ele) ? null : ele,
    time: timeText == null ? null : new Date(timeText),
  };
}

/** Whether the parser found anything to import at all. */
export function hasTracks(tracks: unknown[] | null | undefined): boolean {
  return Array.isArray(tracks) && tracks.length > 0;
}

/**
 * Normalises the parser's points to the importer's shape.
 *
 * A missing elevation becomes 0 rather than undefined because the server's
 * `altitudes` array is positional — a hole in it would shift every later point's
 * height onto the wrong coordinate.
 */
export function toTrackPoints(points: ParsedPoint[] | null | undefined): GpxTrackPoint[] {
  if (!points) return [];

  return points.map((point) => ({
    lat: point.lat,
    lon: point.lon,
    elevation: point.ele || 0,
    time: point.time,
  }));
}

/**
 * The recording's title: the file's <metadata><name> when it has one, else the
 * caller's fallback (which the dialog derives from the filename).
 *
 * The decode is the point. gpxparser reads text elements with `elem.innerHTML`,
 * which serialises the node rather than returning its text, so a name the file
 * wrapped or escaped arrives still wrapped or still escaped. AllTrails wraps
 * its names in CDATA, and one of its exports was imported and listed as
 * "<![CDATA[Afternoon hike at Warrenscourt: Wood Link and Kilmurry Wood Loop]]>".
 *
 * Lives here rather than in the component because ImportRecordingDialog.vue
 * cannot be loaded under Jest at all — gpxparser's CommonJS entry fights the
 * test environment — so a decision left inline there cannot be tested.
 */
export function recordingTitleFrom(
  parsed: { metadata?: { name?: unknown } } | null | undefined,
  fallback: string
): string {
  const metadataName = decodeGpxName(parsed?.metadata?.name);

  return metadataName && metadataName.trim() !== '' ? metadataName : fallback;
}
